﻿const init = (calculator: HTMLElement) => {
  const animalTypeSection = calculator.querySelector<HTMLElement>("[data-name='animal-type-section']");
  const animalSizeSection = calculator.querySelector<HTMLElement>("[data-name='animal-size-section']");
  const animalAgeSection = calculator.querySelector<HTMLElement>("[data-name='animal-age-section']");

  // if there's a particular selection section
  // setup the buttons for that section
  if (animalTypeSection && animalSizeSection && animalAgeSection) {
    const animalTypeSelectionRadioButtons = animalTypeSection.querySelectorAll<HTMLInputElement>("input[type='radio']");
    attachAnimalTypeButtonListeners(animalTypeSelectionRadioButtons, animalSizeSection, animalAgeSection);
  }

  if (animalSizeSection && animalAgeSection) {
    const sizeButtons = animalSizeSection.querySelectorAll<HTMLInputElement>("input[type='radio']");
    attachAnimalSizeButtonListeners(sizeButtons, animalAgeSection);
  }

  if (animalAgeSection) {
    const rangeInputs = calculator.querySelectorAll<HTMLInputElement>("input[type='range']");
    attachAnimalInputRangeListeners(rangeInputs);
  }
};

/**
 * Attaches event listeners to the supplied HTMLInputElements
 * the range inputs should switch the corresponding
 * description panel
 * @param rangeInputs
 */
const attachAnimalInputRangeListeners = (rangeInputs: NodeListOf<HTMLInputElement>) => {
  rangeInputs.forEach(rangeInput => {
    rangeInput.addEventListener("input", event => {
      const delay: number = 600;

      setTimeout(() => {
        const target = (event.target as HTMLInputElement);
        const selectedAnimalAge = target.value;
        const targetSelectionSection = target.closest<HTMLElement>("[data-name='animal-age-selection']");
      
        if (targetSelectionSection) {
          const selectedAnimalType = targetSelectionSection.dataset.animalType;
          const selectedAnimalSize = targetSelectionSection.dataset.size;
        
          const descriptionSections = targetSelectionSection.querySelectorAll<HTMLElement>("[data-name='animal-description']");
        
          const selectedDescriptionSection = Array
            .from(descriptionSections)
            .find(descriptionSection => descriptionSection.dataset.descriptionType == selectedAnimalType &&
              descriptionSection.dataset.descriptionSize == selectedAnimalSize &&
              descriptionSection.dataset.descriptionAge == selectedAnimalAge);
        
          if (selectedDescriptionSection) {
            hideSections(descriptionSections);
            showSection(selectedDescriptionSection);
          }
        }
        else {
          console.error("No description section found which matches target selection");
        }
      }, delay);
    })
  })
}

/**
 * Attaches event listeners to the supplied HTMLInputElements
 * the size buttons should reveal the age section for the corresponding
 * size
 * @param radioButtons
 * @param ageSection
 */
const attachAnimalSizeButtonListeners = (radioButtons: NodeListOf<HTMLElement>, ageSection: HTMLElement) => {
  radioButtons.forEach(sizeButton => {
    sizeButton.addEventListener("click", event => {
      const selectedSize = (event.target as HTMLInputElement).value;
      const selectedAnimal = (event.target as HTMLElement).dataset.animalType;
      
      showSection(ageSection);

      const ageSelectionSections = ageSection.querySelectorAll<HTMLElement>("[data-name='animal-age-selection']");

      if (ageSelectionSections) {
        hideSections(ageSelectionSections);

        const selectedAnimalAgeSection = Array
          .from(ageSelectionSections)
          .find(ageSection => ageSection.dataset.animalType == selectedAnimal && ageSection.dataset.size == selectedSize);
        showSection(selectedAnimalAgeSection);
      }
    });
  })
};

/**
 * Attaches event listeners to the supplied HTMLInputElements
 * The type button should reveal the size section if available,
 * else the age section is displayed
 * whether the size or age section is reveal, only the selections for
 * the specified type should be displayed
 * @param radioButtons
 * @param sizeSection
 * @param ageSection
 */
const attachAnimalTypeButtonListeners = (radioButtons: NodeListOf<HTMLInputElement>, sizeSection: HTMLElement, ageSection: HTMLElement) => {
  radioButtons.forEach(radioButton => {
    radioButton.addEventListener("click", event => {
      const selectedAnimal = (event.target as HTMLInputElement).value;
      
      const sizeSelectionSections = sizeSection.querySelectorAll<HTMLElement>("[data-name='animal-size-selection']");
      const selectedAnimalSection = Array
        .from(sizeSelectionSections)
        .find(sizeSelectionSection => sizeSelectionSection.dataset.animalType == selectedAnimal);
      
      // It's possible that some animal sections
      // don't have size options
      // in those cases, display the age selection section
      if (hasSizeSelectionSection(selectedAnimalSection)) {
        hideSection(ageSection);
        showSection(sizeSection);
        showSection(selectedAnimalSection);

        const sizeSelectionInputs = sizeSection.querySelectorAll<HTMLInputElement>(`input[data-animal-type='${selectedAnimal}']`);
        deselectInputElements(sizeSelectionInputs);
      }
      else {
        hideSection(sizeSection);
        showSection(ageSection);

        const ageSelectionSections = ageSection.querySelectorAll<HTMLElement>("[data-name='animal-age-selection']");
        hideSections(ageSelectionSections);
        const selectedAgeSection = Array
          .from(ageSelectionSections)
          .find(ageSelectionSection => ageSelectionSection.dataset.animalType == selectedAnimal);
        showSection(selectedAgeSection);
      }
    })
  });
};

/**
 * Set each of the supplied input elements' checked attribute to false
 * @param inputElements
 */
const deselectInputElements = (inputElements: NodeListOf<HTMLInputElement> | undefined) => {
  if (inputElements) {
    inputElements.forEach(input => {
      input.checked = false;
    })
  }
}

/**
 * Sets display none on each HTMLElement in the supplied NodeList
 * @param sections
 */
const hideSections = (sections: NodeListOf<HTMLElement> | undefined) => {
  if (sections) {
    sections.forEach(section => {
      hideSection(section);
    });
  }
};

/**
 * Sets display flex on the supplied HTMLElement
 * @param section
 */
const showSection = (section: HTMLElement | undefined) => {
  if (section) {
    section.style.display = "flex";
  }
};

/**
 * Sets display none on the supplied HTMLElement
 * @param section
 */
const hideSection = (section: HTMLElement | undefined) => {
  if (section) {
    section.style.display = "none";
  }
};

/**
 * Check to see if a section contains a radio input
 * @param section
 * @returns true is section contains a input with type radio, otherwise false
 */
const hasSizeSelectionSection = (section: HTMLElement | undefined) => {
  if (!section) {
    return false;
  }

  const sizeSelectionSection = section.querySelector<HTMLInputElement>("input[type='radio']");
  
  if (sizeSelectionSection) {
    return true;
  }

  return false;
};

const ageCalculators = document.querySelectorAll<HTMLElement>("[data-name='age-calculator']");

ageCalculators.forEach(calculator => {
  init(calculator);
});